import React, { useEffect, useState, useRef } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useNavigate } from 'react-router';
import { maps as mapsAPI, stroot } from '@birdi/js-sdk';
import type { ProcessingReport } from '@birdi/js-sdk/src/public/maps';
import { useMetaSelector } from '../../meta/reducer';
import { timeAgo } from '../utils';
import { posthog } from '../../lib/posthog';
import MapSVG from '@birdi/icons/map.svg';
import DownloadSVG from '@birdi/icons/download.svg';
import ChevronDownSVG from '@birdi/icons/chevron-down.svg';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

const REPORTS_PER_PAGE = 3;

type TimePeriod = 'all' | 'last_30_days' | 'last_7_days' | 'today';

const TIME_PERIOD_OPTIONS: { value: TimePeriod; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'today', label: 'Today' },
];

const getDateRange = (
  period: TimePeriod,
): { startDate?: string; endDate?: string } => {
  if (period === 'all') {
    return {};
  }

  const now = new Date();
  const endDate = now.toISOString();

  let startDate: Date;
  switch (period) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'last_7_days':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case 'last_30_days':
    default:
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
  }

  return { startDate: startDate.toISOString(), endDate };
};

export const ProcessingReports: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const meta = useMetaSelector();

  const [reports, setReports] = useState<ProcessingReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const workspaceId = meta.workspace?.id || meta.organisation.id;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchReports = async (limit: number, period: TimePeriod) => {
    if (!meta.organisation?.id || !workspaceId) {
      return { reports: [], hasMore: false };
    }
    const { startDate, endDate } = getDateRange(period);
    // Build options, only including dates if they have values
    const opts: { limit: number; startDate?: string; endDate?: string } = {
      limit: limit + 1,
    };
    if (startDate) opts.startDate = startDate;
    if (endDate) opts.endDate = endDate;

    // Fetch one extra to check if there are more
    const res = await mapsAPI.getProcessingReports(
      stroot('prcrp1'),
      workspaceId,
      opts,
    );
    const fetchedReports = res.body || [];
    const hasMoreItems = fetchedReports.length > limit;
    return {
      reports: hasMoreItems ? fetchedReports.slice(0, limit) : fetchedReports,
      hasMore: hasMoreItems,
    };
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const result = await fetchReports(REPORTS_PER_PAGE, timePeriod);
        setReports(result.reports);
        setHasMore(result.hasMore);
      } catch (e) {
        console.error('Error fetching processing reports:', e);
        setReports([]);
        setHasMore(false);
      }
      setLoading(false);
    })();
  }, [meta.organisation?.id, workspaceId, timePeriod]);

  const handleTimePeriodChange = (period: TimePeriod) => {
    posthog?.capture('dashboard processing reports time period changed', { period });
    setTimePeriod(period);
    setDropdownOpen(false);
  };

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    posthog?.capture('dashboard processing reports expand clicked', {
      timePeriod,
    });

    try {
      const newLimit = reports.length + REPORTS_PER_PAGE;
      const result = await fetchReports(newLimit, timePeriod);
      setReports(result.reports);
      setHasMore(result.hasMore);
    } catch (e) {
      console.error('Error loading more reports:', e);
    }
    setLoadingMore(false);
  };

  const isExpandDisabled =
    reports.length === 0 || !hasMore || loading || loadingMore;

  const handleReportClick = (report: ProcessingReport) => {
    posthog?.capture('dashboard processing reports clicked', {
      reportId: report.id,
      mapId: report.mapId,
    });
    // Navigate to the map
    navigate(`/map/next/${report.mapId}`);
  };

  const handleDownload = async (e: React.MouseEvent, report: ProcessingReport) => {
    e.stopPropagation();
    if (!report.s3Key) return;

    posthog?.capture('dashboard processing report download clicked', {
      reportId: report.id,
    });

    // Get the download URL via the media info API
    try {
      const mediaInfo = await mapsAPI.getLayerMediaInfo(
        stroot('prcrp2'),
        report.mapId,
        report.id,
      );
      if (mediaInfo.body?.fullImageUrl) {
        window.open(mediaInfo.body.fullImageUrl, '_blank');
      }
    } catch (error) {
      console.error('Error getting download URL:', error);
    }
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
        id={suppressIntercomAnchor ? undefined : 'processing-reports'}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Processing reports</span>
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1em;
          `}
        >
          <span>Track the latest processing reports from your maps.</span>
          <div
            ref={dropdownRef}
            css={css`
              position: relative;
            `}
          >
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              css={css`
                display: flex;
                align-items: center;
                gap: 0.5em;
                background: ${theme.mono0};
                border: 1px solid ${theme.mono30};
                border-radius: 6px;
                padding: 0.4em 0.75em;
                cursor: pointer;
                color: ${theme.midnight};
                font-size: 0.9em;
                white-space: nowrap;
                &:hover {
                  background: ${theme.mono10};
                }
              `}
            >
              <span>
                {TIME_PERIOD_OPTIONS.find((o) => o.value === timePeriod)?.label}
              </span>
              <span
                css={css`
                  display: flex;
                  width: 14px;
                  height: 14px;
                  transform: ${dropdownOpen
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)'};
                  transition: transform 0.2s;
                `}
              >
                <ChevronDownSVG />
              </span>
            </button>
            {dropdownOpen && (
              <div
                css={css`
                  position: absolute;
                  top: 100%;
                  right: 0;
                  margin-top: 4px;
                  background: ${theme.mono0};
                  border: 1px solid ${theme.mono30};
                  border-radius: 6px;
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                  z-index: 10;
                  min-width: 140px;
                `}
              >
                {TIME_PERIOD_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleTimePeriodChange(option.value)}
                    css={css`
                      display: block;
                      width: 100%;
                      padding: 0.5em 0.75em;
                      background: ${timePeriod === option.value
                        ? theme.mono10
                        : 'transparent'};
                      border: none;
                      text-align: left;
                      cursor: pointer;
                      color: ${theme.midnight};
                      font-size: 0.9em;
                      &:hover {
                        background: ${theme.mono10};
                      }
                      &:first-of-type {
                        border-radius: 6px 6px 0 0;
                      }
                      &:last-of-type {
                        border-radius: 0 0 6px 6px;
                      }
                    `}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Empty state */}
        {!loading && reports.length === 0 && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
            `}
          >
            <div>
              <strong>Your latest processing reports will appear here.</strong>
            </div>
            <div
              css={css`
                margin-top: 0.5em;
              `}
            >
              Process new imagery or re-run a map to start tracking report updates in this space.
            </div>
          </div>
        )}

        {/* Reports list */}
        {!loading && reports.length > 0 && (
          <div>
            {reports.map((report) => (
              <div
                key={report.id}
                onClick={() => handleReportClick(report)}
                css={css`
                  display: flex;
                  flex-direction: column;
                  background: ${theme.mono0};
                  border-radius: 8px;
                  border: 1px solid ${theme.mono30};
                  margin-bottom: 0.5em;
                  padding: 1em;
                  cursor: pointer;
                  transition:
                    box-shadow 0.15s,
                    background 0.15s;
                  &:hover {
                    background: ${theme.mono20};
                    border: none;
                  }
                `}
              >
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 0.25em;
                  `}
                >
                  <div
                    css={css`
                      font-weight: 700;
                      color: ${theme.midnight};
                      font-size: 1.05em;
                    `}
                  >
                    {report.name || 'Untitled Report'}
                  </div>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 0.5em;
                      padding: 0.25em 0.75em;
                      border-radius: 4px;
                      font-size: 0.85em;
                      color: ${theme.midnight};
                      background: ${report.status === 'Ready'
                        ? '#E6F4E6'
                        : '#FFF4D5'};
                      color: ${report.status === 'Ready'
                        ? '#4AD294'
                        : '#E6A100'};
                    `}
                  >
                    <span>{report.status}</span>
                  </div>
                </div>

                <div
                  css={css`
                    color: ${theme.midnight};
                    margin-bottom: 0.25em;
                  `}
                >
                  {report.creatorName || 'Unknown user'}
                </div>

                <div
                  css={css`
                    color: ${theme.midnight};
                    margin-bottom: 0.5em;
                  `}
                >
                  {report.createdAt ? timeAgo(report.createdAt) : ''}
                </div>

                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 0.5em;
                      color: ${theme.mono50};
                      font-size: 0.9em;
                    `}
                  >
                    <span
                      css={css`
                        display: flex;
                        width: 14px;
                        height: 14px;
                      `}
                    >
                      <MapSVG />
                    </span>
                    <span>{report.mapName || 'Untitled Map'}</span>
                  </div>

                  {report.s3Key && report.status === 'Ready' && (
                    <button
                      onClick={(e) => handleDownload(e, report)}
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.25em;
                        background: none;
                        border: none;
                        padding: 0;
                        color: ${theme.deepOcean};
                        cursor: pointer;
                        font-size: 0.9em;
                        text-decoration: underline;
                        &:hover {
                          color: ${theme.ocean};
                        }
                      `}
                    >
                      <span
                        css={css`
                          display: flex;
                          width: 14px;
                          height: 14px;
                        `}
                      >
                        <DownloadSVG />
                      </span>
                      Download PDF
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
            `}
          >
            Loading...
          </div>
        )}
      </div>

      <div
        css={css`
          padding: 1em;
        `}
      >
        <button
          onClick={handleLoadMore}
          disabled={isExpandDisabled}
          css={css`
            width: 100%;
            color: ${isExpandDisabled ? theme.mono40 : theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 5px;
            padding: 0.5em;
            font-weight: 500;
            cursor: ${isExpandDisabled ? 'not-allowed' : 'pointer'};
            background: ${theme.mono0};
            opacity: ${isExpandDisabled ? 0.6 : 1};
          `}
        >
          {loadingMore ? 'Loading...' : 'Expand'}
        </button>
      </div>
    </div>
  );
};

